/* eslint-disable global-require */
import { useEffect, useRef, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Popover
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'src/redux/authSlice';
import api from 'src/redux/config';
import { useTranslation } from 'react-i18next';
import { getProfile } from 'src/redux/authSlice';

const UserBoxButton = styled(Button)(
  () => `
    padding: 0 8px 0 0;
    min-width: auto;
    border-radius: 999px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  `
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(2)};
  `
);

function HeaderUserbox() {
  const { t, i18n } = useTranslation();
  const [imageData, setImageData] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const ref = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((x) => x.client);

  // ✅ Au montage : si on a un token mais pas de profil, on va le chercher
  useEffect(() => {
    if (authUser?.accessToken && !authUser?.profile?.firstname && !authUser?.profile?.lastname && !authUser?.profile?.email) {
      dispatch(getProfile());
    }
  }, [authUser?.accessToken, authUser?.profile, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  /** NOM ROBUSTE : plusieurs fallbacks pour que quelque chose s'affiche dès /home */
  const displayName = useMemo(() => {
    const p = authUser?.profile || {};
    const u = authUser?.user || {};
    const full =
      [p?.firstname, p?.lastname].filter(Boolean).join(' ').trim();
    return (
      full ||
      p?.username ||
      authUser?.email ||
      p?.email ||
      u?.email ||
      localStorage.getItem('email') ||
      ''
    );
  }, [authUser]);

  /** ✅ SOLUTION FINALE: Gérer la réponse "NO_PICTURE" du backend */
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!authUser?.accessToken) return;

      try {
        const response = await api.get(`/user/profile-picture`, {
          headers: { 
            Authorization: `Bearer ${authUser.accessToken}`
          },
          responseType: 'arraybuffer'
        });

        // ✅ Vérifier si la réponse est une vraie image ou un message d'erreur JSON
        const contentType = response.headers['content-type'];
        
        if (contentType && contentType.includes('application/json')) {
          // C'est un JSON (probablement "NO_PICTURE"), pas une image
          setImageData(null);
          return;
        }

        // C'est une vraie image
        const arrayBufferView = new Uint8Array(response.data);
        const blob = new Blob([arrayBufferView], { type: 'image/jpeg' });
        const imageUrl = URL.createObjectURL(blob);
        setImageData(imageUrl);
        
      } catch (error) {
        // ✅ Gérer l'erreur 400 "NO_PICTURE" silencieusement
        if (error.response?.status === 400) {
          // Vérifier si c'est le message "NO_PICTURE"
          try {
            const text = new TextDecoder().decode(error.response.data);
            const json = JSON.parse(text);
            if (json.message === 'NO_PICTURE') {
              // C'est normal, pas une vraie erreur
              setImageData(null);
              return;
            }
          } catch (e) {
            // Pas du JSON, ignorer
          }
        }
        
        // Autres cas : pas d'erreur affichée, juste utiliser l'image par défaut
        setImageData(null);
      }
    };

    fetchProfilePicture();
  }, [authUser?.accessToken]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      {/* Bouton barre sup. : avatar + (nom visible quand ouvert) */}
      <UserBoxButton
        color="secondary"
        ref={ref}
        onClick={handleOpen}
        sx={{
          borderRadius: isOpen ? '999px' : '50%',
          paddingRight: isOpen ? 1 : 0
        }}
      >
        {imageData ? (
          <Avatar
            variant="rounded"
            alt={displayName}
            src={imageData}
            style={{ borderRadius: 100, width: 32, height: 32 }}
          />
        ) : (
          <Avatar
            variant="rounded"
            alt="profile"
            src={require('../../../../assets/images/profile.png')}
            style={{ width: 32, height: 32 }}
          />
        )}

        {isOpen && !!displayName && (
          <Box
            sx={{
              fontWeight: 700,
              maxWidth: 160,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden'
            }}
            title={displayName}
          >
            {displayName}
          </Box>
        )}
      </UserBoxButton>

      {/* Popover : avatar + nom + actions */}
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: i18n.dir() === 'rtl' ? 'left' : 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: i18n.dir() === 'rtl' ? 'left' : 'right'
        }}
      >
        <MenuUserBox
          sx={{ minWidth: 210 }}
          display="flex"
          gap={1}
          alignItems="center"
        >
          {imageData ? (
            <Avatar variant="rounded" alt={displayName} src={imageData} />
          ) : (
            <Avatar
              variant="rounded"
              alt="profile"
              src={require('../../../../assets/images/profile.png')}
            />
          )}
          <Box
            sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={displayName}
          >
            {displayName}
          </Box>
        </MenuUserBox>

        <List sx={{ p: 1 }} component="nav">
          <ListItem button to="/profil" component={NavLink}>
            <AccountBoxTwoToneIcon fontSize="small" />
            <ListItemText primary={t('My Profile')} />
          </ListItem>

          <ListItem button onClick={handleLogout}>
            <LockOpenTwoToneIcon fontSize="small" />
            <ListItemText primary={t('Sign out')} />
          </ListItem>
        </List>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
